import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, UserPlus } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { inviteMember, type MemberRole } from '../api/members-api'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { supabase } from '@/lib/supabase/client'
import { createClient } from '@supabase/supabase-js'

const ROLE_OPTIONS: { value: MemberRole; label: string }[] = [
  { value: 'admin', label: 'مدیر' },
  { value: 'data_entry', label: 'ورود داده' },
  { value: 'approver', label: 'بررسی‌کننده' },
  { value: 'viewer', label: 'مشاهده‌گر' },
]

interface InviteMemberModalProps {
  projectId: string
  open: boolean
  onClose: () => void
}

interface FoundUser {
  id: string
  email: string
  name: string
}

// ── Existing-user tab ────────────────────────────────────────────
function SearchTab({
  projectId,
  onSuccess,
}: {
  projectId: string
  onSuccess: () => void
}) {
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [search, setSearch] = useState('')
  const [foundUser, setFoundUser] = useState<FoundUser | null>(null)
  const [role, setRole] = useState<MemberRole>('data_entry')
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [inviteError, setInviteError] = useState<string | null>(null)

  const doSearch = async () => {
    if (!search.trim()) return
    setSearching(true)
    setSearchError(null)
    setFoundUser(null)
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('id, full_name')
        .ilike('full_name', `%${search}%`)
        .limit(5)
      const match = (data ?? [])[0]
      if (match) {
        setFoundUser({ id: match.id, email: search, name: match.full_name ?? search })
      } else {
        setSearchError('کاربری با این مشخصات یافت نشد.')
      }
    } finally {
      setSearching(false)
    }
  }

  const inviteMutation = useMutation({
    mutationFn: () => inviteMember(projectId, foundUser!.id, role, user!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members', projectId] })
      onSuccess()
    },
    onError: (e) => setInviteError((e as Error).message),
  })

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>نام کاربر</Label>
        <div className="flex gap-2">
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setFoundUser(null) }}
            placeholder="جستجو بر اساس نام..."
            onKeyDown={(e) => e.key === 'Enter' && doSearch()}
          />
          <Button variant="outline" size="icon" onClick={doSearch} disabled={searching}>
            <Search className="w-4 h-4" />
          </Button>
        </div>
        {searchError && <p className="text-xs text-destructive">{searchError}</p>}
      </div>

      {foundUser && (
        <div className="bg-muted rounded-lg px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
            <span className="text-xs font-medium text-primary">{foundUser.name[0]}</span>
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">{foundUser.name}</p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label>نقش</Label>
        <Select value={role} onValueChange={(v) => setRole(v as MemberRole)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {ROLE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {inviteError && <p className="text-sm text-destructive">{inviteError}</p>}

      <DialogFooter>
        <Button
          onClick={() => inviteMutation.mutate()}
          disabled={!foundUser || inviteMutation.isPending}
          className="w-full"
        >
          {inviteMutation.isPending ? 'در حال افزودن...' : 'افزودن به پروژه'}
        </Button>
      </DialogFooter>
    </div>
  )
}

// ── Create-new-user tab ──────────────────────────────────────────
function CreateTab({
  projectId,
  onSuccess,
}: {
  projectId: string
  onSuccess: () => void
}) {
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<MemberRole>('data_entry')
  const [error, setError] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: async () => {
      setError(null)
      // Use a temp client so the current session is not replaced
      const tempClient = createClient(
        import.meta.env.VITE_SUPABASE_URL as string,
        import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        { auth: { persistSession: false, autoRefreshToken: false } }
      )
      const { data: signUpData, error: signUpError } = await tempClient.auth.signUp({
        email: email.trim(),
        password,
        options: { data: { full_name: fullName.trim() } },
      })
      if (signUpError) throw signUpError
      const newUserId = signUpData.user?.id
      if (!newUserId) throw new Error('ایجاد کاربر ناموفق بود.')
      await inviteMember(projectId, newUserId, role, user!.id)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members', projectId] })
      onSuccess()
    },
    onError: (e) => setError((e as Error).message),
  })

  const canSubmit = fullName.trim() && email.trim() && password.length >= 6

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>نام و نام‌خانوادگی</Label>
        <Input
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="علی محمدی"
        />
      </div>
      <div className="space-y-2">
        <Label>ایمیل</Label>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="ali@example.com"
          dir="ltr"
        />
      </div>
      <div className="space-y-2">
        <Label>رمز عبور</Label>
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="حداقل ۶ کاراکتر"
          dir="ltr"
        />
      </div>
      <div className="space-y-2">
        <Label>نقش</Label>
        <Select value={role} onValueChange={(v) => setRole(v as MemberRole)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {ROLE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <DialogFooter>
        <Button
          onClick={() => createMutation.mutate()}
          disabled={!canSubmit || createMutation.isPending}
          className="w-full"
        >
          {createMutation.isPending ? 'در حال ایجاد...' : 'ایجاد کاربر و افزودن به پروژه'}
        </Button>
      </DialogFooter>
    </div>
  )
}

// ── Main modal ───────────────────────────────────────────────────
export function InviteMemberModal({ projectId, open, onClose }: InviteMemberModalProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            افزودن عضو
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="search" dir="rtl">
          <TabsList className="w-full">
            <TabsTrigger value="search" className="flex-1">کاربر موجود</TabsTrigger>
            <TabsTrigger value="create" className="flex-1">ایجاد کاربر جدید</TabsTrigger>
          </TabsList>
          <TabsContent value="search" className="mt-4">
            <SearchTab projectId={projectId} onSuccess={onClose} />
          </TabsContent>
          <TabsContent value="create" className="mt-4">
            <CreateTab projectId={projectId} onSuccess={onClose} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
